import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Clock3,
  LayoutGrid,
  PenLine,
  RefreshCw,
} from 'lucide-react';
import HomeCreationIntake from './HomeCreationIntake';
import VisualLinkCard from '../components/VisualLinkCard';
import { getPublishedVideoCases } from '../learn/videoCaseApi';
import useHomeData from './useHomeData';
import AnimatedBrandText from './AnimatedBrandText';
import { useTeamBrand } from '../branding/TeamBrandContext';

const HOME_CASE_LIMIT = 4;

function greetingWord() {
  const hour = new Date().getHours();
  if (hour < 5) return '夜深了';
  if (hour < 12) return '早上好';
  if (hour < 18) return '下午好';
  return '晚上好';
}

function timeAgo(value) {
  if (!value) return '最近编辑';
  const seconds = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000);
  if (!Number.isFinite(seconds)) return '最近编辑';
  if (seconds < 60) return '刚刚';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟前`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} 小时前`;
  return `${Math.floor(seconds / 86400)} 天前`;
}

function RecentSkeleton() {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label="正在加载最近项目">
      {[0, 1, 2].map(item => (
        <div key={item} className="h-[116px] animate-pulse rounded-[20px] border border-black/[0.05] bg-white/55 dark:border-white/[0.06] dark:bg-white/[0.04]" />
      ))}
    </div>
  );
}

function EntryCardsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" aria-label="正在加载精选案例">
      {Array.from({ length: HOME_CASE_LIMIT }, (_, index) => (
        <div
          key={index}
          className="min-h-[218px] animate-pulse rounded-[24px] border border-black/[0.05] bg-white/55 dark:border-white/[0.06] dark:bg-white/[0.04] xl:aspect-[4/3] xl:min-h-0"
        />
      ))}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const brand = useTeamBrand();
  const outletContext = useOutletContext() || {};
  const { user, userLoading, preview = false } = outletContext;
  const authenticated = preview || Boolean(user) || Boolean(localStorage.getItem('token'));
  const personalizedGreeting = preview || Boolean(user);
  const creationOwner = user?.id ? String(user.id) : user?.username || (authenticated ? 'authenticated' : 'guest');
  const { loading, recent, failedSources, retry } = useHomeData({ preview, authenticated });
  const [entryCards, setEntryCards] = useState([]);
  const [entryLoading, setEntryLoading] = useState(true);
  const [entryError, setEntryError] = useState('');
  const inputRef = useRef(null);

  const loadEntryCards = useCallback(async () => {
    setEntryLoading(true);
    setEntryError('');
    try {
      const cases = await getPublishedVideoCases();
      const orderedCases = [...cases].sort((left, right) => {
        const sortDifference = Number(right.sort || 0) - Number(left.sort || 0);
        if (sortDifference !== 0) return sortDifference;
        return Number(right.id || 0) - Number(left.id || 0);
      });
      setEntryCards(orderedCases.slice(0, HOME_CASE_LIMIT));
    } catch (error) {
      setEntryCards([]);
      setEntryError(getErrorMessage(error, '精选案例加载失败'));
    } finally {
      setEntryLoading(false);
    }
  }, []);

  useEffect(() => { loadEntryCards(); }, [loadEntryCards]);

  const greetingName = useMemo(() => {
    if (userLoading) return '创作者';
    return user?.username || '创作者';
  }, [user, userLoading]);

  const openEntry = href => {
    if (preview && (href.startsWith('/learn') || href.startsWith('/writing'))) {
      navigate(`/prototype/vnext${href}`);
      return;
    }
    if (!authenticated && !href.startsWith('/learn')) {
      navigate('/login', { state: { from: href } });
      return;
    }
    navigate(href);
  };

  return (
    <div className="relative min-h-full overflow-hidden bg-[var(--vnext-page)]">
      <div className="relative mx-auto max-w-[1240px] px-5 pb-16 pt-6 sm:px-8 lg:px-12">
        <section className="mx-auto max-w-[980px] pb-8 pt-5 text-center md:pt-7">
          <div className="mb-5 inline-flex items-center gap-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400 dark:text-white/34">
            <span className="h-px w-5 bg-black/[0.16] dark:bg-white/[0.18]" aria-hidden="true" />
            从一个想法开始
            <span className="h-px w-5 bg-black/[0.16] dark:bg-white/[0.18]" aria-hidden="true" />
          </div>
          <h1 className="text-[26px] font-semibold leading-[1.3] tracking-[-0.045em] text-[#26222a] dark:text-[#f4f0f7] md:text-[28px]">
            {personalizedGreeting ? (
              <>{greetingWord()}，{greetingName}</>
            ) : (
              <>把灵感交给 <AnimatedBrandText /></>
            )}
          </h1>
          <p className="mt-3 text-[13px] leading-6 text-stone-500 dark:text-white/55">一个故事，一条视频，都可以从这里开始。</p>
          {userLoading && !preview ? <p className="my-8 text-[12px] text-stone-500" role="status">正在准备创作入口…</p>
            : <HomeCreationIntake key={creationOwner} authenticated={authenticated} preview={preview} owner={creationOwner} inputRef={inputRef} onReview={() => openEntry('/writing?tab=review&mode=upload')} />}
        </section>

        <section aria-labelledby="entry-heading">
          <div className="mb-2 flex items-end justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400 dark:text-white/28">Create your way</p>
              <h2 id="entry-heading" className="mt-1 text-[20px] font-semibold tracking-[-0.03em]">从这里开始</h2>
            </div>
          </div>
          {entryLoading ? (
            <EntryCardsSkeleton />
          ) : entryError ? (
            <div className="grid min-h-[218px] place-items-center rounded-[24px] border border-dashed border-black/[0.1] bg-white/30 px-6 text-center dark:border-white/[0.09] dark:bg-white/[0.02]" role="alert">
              <div>
                <p className="text-[13px] font-medium">精选案例暂时没有加载成功</p>
                <p className="mt-1.5 text-[11px] text-stone-400 dark:text-white/34">{entryError}</p>
                <button type="button" onClick={loadEntryCards} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]">
                  <RefreshCw size={12} />重新加载
                </button>
              </div>
            </div>
          ) : entryCards.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {entryCards.map(card => (
                <VisualLinkCard
                  key={card.id}
                  image={card.cover || card.image_url || ''}
                  eyebrow={card.tag || card.category_name || card.category || '精选案例'}
                  title={card.title}
                  description={card.sub_title || card.creation_tags || '查看完整案例与创作方法。'}
                  onClick={() => navigate(preview ? '/prototype/vnext/learn' : `/learn/${encodeURIComponent(card.id)}`)}
                  className="xl:aspect-[4/3] xl:min-h-0"
                />
              ))}
            </div>
          ) : (
            <div className="grid min-h-[218px] place-items-center rounded-[24px] border border-dashed border-black/[0.1] bg-white/30 px-6 text-center dark:border-white/[0.09] dark:bg-white/[0.02]">
              <div>
                <BookOpen size={21} className="mx-auto mb-3 text-[#8c7db0]" />
                <p className="text-[13px] font-medium">暂无已上架的精选案例</p>
                <button type="button" onClick={() => navigate(preview ? '/prototype/vnext/learn' : '/learn')} className="mt-2 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]">前往学习与案例</button>
              </div>
            </div>
          )}
        </section>

        <section className="mt-12" aria-labelledby="recent-heading">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400 dark:text-white/28">Continue creating</p>
              <h2 id="recent-heading" className="mt-1 text-[20px] font-semibold tracking-[-0.03em]">最近项目</h2>
            </div>
            {failedSources.length > 0 && (
              <button
                type="button"
                onClick={retry}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-stone-500 transition hover:text-stone-900 dark:text-white/38 dark:hover:text-white"
              >
                <RefreshCw size={12} />
                {failedSources.join('、')}暂未加载，重试
              </button>
            )}
          </div>

          {!authenticated ? (
            <div className="flex min-h-[144px] flex-col items-center justify-center rounded-[22px] border border-dashed border-black/[0.1] bg-white/30 px-6 text-center dark:border-white/[0.09] dark:bg-white/[0.02]">
              <BookOpen size={21} className="mb-3 text-[#8c7db0]" />
              <p className="text-[13px] font-medium">登录后查看并继续最近项目</p>
              <button
                type="button"
                onClick={() => navigate('/login', { state: { from: '/home' } })}
                className="mt-2 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]"
              >
                {brand.text('登录小影')}
              </button>
            </div>
          ) : loading ? (
            <RecentSkeleton />
          ) : recent.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {recent.map((item, index) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => navigate(item.href)}
                  className="group flex min-h-[116px] items-center gap-4 rounded-[20px] border border-black/[0.06] bg-white/58 p-4 text-left shadow-[0_18px_44px_-38px_rgba(54,45,36,.65)] backdrop-blur transition hover:-translate-y-0.5 hover:border-[#8c7db0]/25 hover:bg-white dark:border-white/[0.07] dark:bg-white/[0.035] dark:hover:border-[#b9a7e7]/25 dark:hover:bg-white/[0.055]"
                >
                  <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] ${
                    index % 3 === 0
                      ? 'bg-[#e8e1ef] text-[#74628f] dark:bg-[#766395]/25 dark:text-[#c8b8ea]'
                      : index % 3 === 1
                        ? 'bg-[#dfe8e2] text-[#667b6e] dark:bg-[#688074]/20 dark:text-[#a9c3b2]'
                        : 'bg-[#ebe5db] text-[#7c6e5d] dark:bg-[#84725d]/20 dark:text-[#c8b79f]'
                  }`}>
                    {item.type === 'writing' ? <PenLine size={21} /> : <LayoutGrid size={21} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold text-stone-800 dark:text-white/86">{item.title}</span>
                    <span className="mt-1.5 flex items-center gap-2 text-[11px] text-stone-400 dark:text-white/34">
                      <span>{item.type === 'writing' ? '剧本' : '画布'}</span>
                      <span>·</span>
                      <span>{item.stage}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><Clock3 size={11} />{timeAgo(item.time)}</span>
                    </span>
                  </span>
                  <ArrowRight size={15} className="text-stone-300 transition group-hover:translate-x-0.5 group-hover:text-[#7a679c] dark:text-white/18 dark:group-hover:text-[#c8b8ea]" />
                </button>
              ))}
            </div>
          ) : (
            <div className="flex min-h-[144px] flex-col items-center justify-center rounded-[22px] border border-dashed border-black/[0.1] bg-white/30 px-6 text-center dark:border-white/[0.09] dark:bg-white/[0.02]">
              {failedSources.length === 2 ? (
                <>
                  <RefreshCw size={20} className="mb-3 text-stone-400" />
                  <p className="text-[13px] font-medium">项目列表暂时没有加载成功</p>
                  <button type="button" onClick={retry} className="mt-2 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]">重新加载</button>
                </>
              ) : (
                <>
                  <BookOpen size={21} className="mb-3 text-[#8c7db0]" />
                  <p className="text-[13px] font-medium">还没有项目，从上面的一个想法开始吧</p>
                  <button type="button" onClick={() => inputRef.current?.focus()} className="mt-2 text-[12px] font-semibold text-[#6f6196] dark:text-[#c8b8ea]">聊聊第一个想法</button>
                </>
              )}
            </div>
          )}
        </section>

        <footer className="mt-14 border-t border-black/[0.06] py-6 text-[10px] text-stone-400 dark:border-white/[0.06] dark:text-white/24">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{brand.text('小影 · 为短剧创作者而生')}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span>{brand.active ? brand.name : 'xiaoying.studio'}</span>
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className="transition hover:text-stone-600 dark:hover:text-white/50"
            >
              苏ICP备2026053046号-1
            </a>
            {/* 公安备案上线后替换此占位 */}
            <span className="opacity-0 select-none" aria-hidden="true">公安备案</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
